import { useState, useEffect } from 'react';
import { TextField, Container, Grid, Paper, Box, Typography, InputAdornment, IconButton, Button } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../remote/authRequest';
import Application from './Application'
import PurpleButton from '../components/LayoutComponents/PurpleButton';
import { reCaptcha } from '../utils/reCaptcha';
import Logo from '../components/LayoutComponents/Logo';
import Background from '../components/LayoutComponents/Background';
import theme from '../components/LayoutComponents/theme';

export default function LoginPage({ isMobile }) {
  const [session, setSession] = useState(null);
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [captchaValue, setCaptchaValue] = useState(null);

  const refreshCaptcha = () => {
    reCaptcha(
      "login",
      (token) => setCaptchaValue(token)
    );
  };

  // Call refreshCaptcha whenever you want a new token
  useEffect(() => {
    // Initial captcha load
    if (!captchaValue) {
      refreshCaptcha();

      // Set up interval to refresh every 2 minutes (120000 ms)
      const intervalId = setInterval(() => {
        refreshCaptcha();
      }, 120000);
      // Clean up on unmount
      return () => clearInterval(intervalId);
    }
  }, [captchaValue]);

  const handleLogin = async (event) => {
    event.preventDefault();

    setErrors({ username: "", password: "" });
    let hasErrors = false;

    if (!username) {
      setErrors((prev) => ({ ...prev, username: "Username is required" }));
      hasErrors = true;
    }
    if (!password) {
      setErrors((prev) => ({ ...prev, password: "Password is required" }));
      hasErrors = true;
    }
    if (hasErrors) return;

    try {
      const res = await loginRequest(username, password, captchaValue);
      console.log(res);
      setSession(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        alert("Invalid username or password");
      } else if (error.response?.status === 401) {
        alert("Confirm your email address, please check your inbox. If you don't see the email, check your spam folder.");
      } else {
        alert("Server error, please try again later");
      }
    } finally {
      refreshCaptcha();
    }
  };

  const handleLogout = () => {
    setSession(null);
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return session ? (
    <Application session={session} handleLogout={handleLogout} isMobile={isMobile} />
  ) : (
    <Background>
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile ? "center" : "flex-start",
          mt: "3rem",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: { xs: 2, sm: 3 },
            borderRadius: 2,
            maxWidth: 380,
            width: "100%",
            alignItems: "center",
            p: { xs: 2, sm: 3 },
            boxSizing: "border-box",
            backgroundColor: '#eef1ff',
          }}
        >
          <Logo />
          <Typography variant="h5" align="center" sx={{ color: theme.colors.purple, mb: 2 }}>
            Login
          </Typography>
          <form onSubmit={handleLogin}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              onChange={(e) => setUsername(e.currentTarget.value)}
              helperText={errors.username}
              error={Boolean(errors.username)}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.currentTarget.value)}
              helperText={errors.password}
              error={Boolean(errors.password)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box mt={2} display="flex" justifyContent="center">
              <PurpleButton label="Login" type="submit" />
            </Box>
          </form>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate("/forgotPassword")}
                sx={{ color: theme.colors.blue }}
              >
                Forgot Password
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate("/registration")}
                sx={{ color: theme.colors.blue }}
              >
                Sign Up
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Background>
  );

}